import type { Organization } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { BusyButton, Field } from '../components/Field.js';

/**
 * 企业信息。初始化那一屏填的东西，之后在这儿看和改。
 *
 * 分成两半是刻意的：**企业标识改不了**——它是这套部署的身份（实例身份、
 * 访问地址、对外称谓）。C-198 起 ATC 编码不再由它派生（ADR-0052：卡号为
 * `ATC-nn-nnn`，部门段 + 部门内序号）；它仍不可改，理由是身份不是编码链。
 * 界面上把它摆出来但不给输入框，比藏起来好：藏起来只会让人以为系统里没这个东西。
 */
export function OrganizationPage({
  onSaved,
}: {
  /** 名称或标识变了，外层要重新拉一次 bootstrap——侧栏和面包屑上都显示着它。 */
  onSaved: () => void;
}): ReactNode {
  const [loaded, setLoaded] = useState<Organization | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [timezone, setTimezone] = useState('');
  const [emailDomain, setEmailDomain] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  function fill(organization: Organization): void {
    setLoaded(organization);
    setName(organization.name);
    setShortName(organization.shortName ?? '');
    setBaseUrl(organization.baseUrl);
    setTimezone(organization.timezone);
    setEmailDomain(organization.emailDomain ?? '');
  }

  useEffect(() => {
    void api.organization().then(fill, (cause: unknown) => setLoadError(messageOf(cause)));
  }, []);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      fill(
        await api.saveOrganization({
          name: name.trim(),
          shortName: shortName.trim(),
          baseUrl: baseUrl.trim(),
          timezone: timezone.trim(),
          emailDomain: emailDomain.trim(),
        }),
      );
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      onSaved();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  if (loadError !== null) return <p className="msg">{loadError}</p>;
  if (loaded === null) return <p className="sub-hint">读取中……</p>;

  return (
    <div className="content-in">
      <div className="page-head">
        <div>
          <h1>企业</h1>
          <p className="sub-hint">初始化时填的信息在这儿改。企业标识建好之后不能改。</p>
        </div>
      </div>

      <form onSubmit={(event) => void submit(event)} className="org-form">
        <Field id="org-code" label="企业标识" aux="不能改">
          <output className="code-preview" id="org-code">
            {loaded.code}
          </output>
        </Field>
        {/*
          C-198 补 2：企业标识保留（实例身份、访问地址、对外称谓），但不再派生
          ATC 编码——卡号规则是 ATC-nn-nnn（部门段 + 序号，ADR-0052）。
          指向落在注释里：判据编号不上产品页面（page-copy 守门）。
        */}
        <p className="sub-hint">
          企业标识是这套部署的身份——实例身份、访问地址与对外称谓用它。
          ATC 编码不再由它派生：卡号按「ATC-部门编码-序号」发放（如 ATC-11-001）。
          标识建好之后不能改，要改只能走数据迁移。
        </p>

        <Field id="org-name" label="企业名称">
          <input id="org-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field id="org-short" label="简称" aux="可不填">
          <input
            id="org-short"
            value={shortName}
            onChange={(event) => setShortName(event.target.value)}
          />
        </Field>

        <Field id="org-url" label="系统访问地址" aux="邀请链接和二维码都用它拼绝对地址">
          <input
            id="org-url"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
          />
        </Field>
        <p className="sub-hint">
          本地化部署，服务端不知道自己对外是什么地址，所以要存下来。换了机器或域名记得改这儿，
          否则发出去的链接会指向旧地址。
        </p>

        <Field id="org-tz" label="时区" aux="IANA 时区名。业务日按它归日">
          <input
            id="org-tz"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
          />
        </Field>

        <Field id="org-domain" label="邮箱域名限制" aux="可不填，清空就是不限制">
          <input
            id="org-domain"
            value={emailDomain}
            onChange={(event) => setEmailDomain(event.target.value)}
          />
        </Field>
        <p className="sub-hint">
          填了之后只能添加这个域名下的邮箱。它只防手滑，不是安全边界；
          改这一项也不影响已经在系统里的人。
        </p>

        {error !== null && <p className="msg">{error}</p>}

        <div className="row-actions">
          {saved && <span className="chip ok">已保存</span>}
          <BusyButton busy={busy} busyLabel="保存中" type="submit">
            保存
          </BusyButton>
        </div>
      </form>
    </div>
  );
}
